import { Formik, FormikHelpers as FormikActions } from 'formik'
import React, { useEffect } from 'react'
import { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router-dom'
import * as Yup from 'yup'
import { ValidationError } from 'epc-schema'
import { Form, Indicators, Input } from 'ui'
import { OptionalString, RequiredString } from 'validations'
import { useCurrentProfile, useUpdateProfile } from '../graphql'

/* BEGIN - Form Values */
/**
 * This interface represents potential form values and
 * is passed into our custom form field factories to get
 * type safety on our form.
 */
interface IEducatorStepTwoFormValues {
  [key: string]: any | string | string[] | undefined
  secondaryLanguages?: string[] | undefined
  id: string
  otherSecondaryLanguages?: string | undefined
}

// tslint:disable object-literal-sort-keys
/**
 * This schema is used by formik to provide client side
 * form validation.
 */
const EducatorStepTwoSchema = Yup.object().shape({
  otherSecondaryLanguages: OptionalString().when('secondaryLanguages', {
    is: (secondaryLanguages) => secondaryLanguages.indexOf('Other') >= 0,
    then: RequiredString(),
  }),
  secondaryLanguages: Yup.array().of(RequiredString()),
})
// tslint:enable object-literal-sort-keys

/**
 * These are the actual default values supplied to the form
 * if a profile is not loaded via the network. Any missing
 * values from the network fetched profile will have these
 * values substituted as well.
 */
const blankValues: IEducatorStepTwoFormValues = {
  id: '',
  otherSecondaryLanguages: '',
  secondaryLanguages: [],
}
/* END - Form Values */

/* BEGIN - Custom Form Renderers */
const FormField = Input.Renderer<IEducatorStepTwoFormValues>()
const CheckBoxGroup = Input.CheckBoxGroupRenderer<IEducatorStepTwoFormValues>()
const ErrorSummary = Form.ErrorSummaryRenderer<IEducatorStepTwoFormValues>()
/* END - Custom Form Renderers */

/**
 * The second step for creating an account as a teacher.
 */
const FormView: React.FC<RouteComponentProps> = (props) => {
  const [updateProfile, { error: mutationError }] = useUpdateProfile()

  const handleSubmitForm = async (
    values: IEducatorStepTwoFormValues,
    actions: FormikActions<any>
  ) => {
    const otherSecondaryLanguages =
      typeof values.otherSecondaryLanguages === 'string'
        ? values.otherSecondaryLanguages.split(',')
        : values.otherSecondaryLanguages
    const result = await updateProfile({
      variables: {
        profileInput: {
          id: values.id as string,
          otherSecondaryLanguages,
          secondaryLanguages: (values.secondaryLanguages || []) as string[],
        },
      },
    })
    if (
      result.data &&
      !result.data.response.success &&
      result.data.response.errors
    ) {
      result.data.response.errors
        .map((e) => e as ValidationError)
        .forEach((fieldError) =>
          actions.setFieldError(`${fieldError.path}`, fieldError.message)
        )
    } else {
      window.location.href = '/app/dashboard'
      return
    }
    actions.setSubmitting(false)
  }

  const {
    error: queryError,
    loading,
    educator,
    getCurrentProfile,
    profileErrors,
  } = useCurrentProfile()

  useEffect(() => {
    getCurrentProfile()
  }, [getCurrentProfile])

  if (loading) {
    return <Indicators.Spinner />
  }

  if (queryError) {
    return (
      <Form.ErrorComponent error={queryError ? queryError.message : null} />
    )
  }

  if (profileErrors) {
    return (
      <Form.ErrorComponent
        error={(profileErrors as ValidationError[])[0].message}
      />
    )
  }

  const defaultValues = (educator as IEducatorStepTwoFormValues) || blankValues
  const initialValues = Object.keys(defaultValues).reduce(
    (o, k) => ({ ...o, [k]: defaultValues[k] || o[k] }),
    blankValues
  )

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmitForm}
      validationSchema={EducatorStepTwoSchema}
    >
      {({
        errors,
        handleSubmit,
        isSubmitting,
        submitCount,
        submitForm,
        values,
      }) => {
        return (
          <Form.Form onSubmit={handleSubmit}>
            <Form.Row>
              <Form.Item>
                <CheckBoxGroup
                  required={false}
                  label="Secondary Language Preferences"
                  description="In addition to English, indicate other languages in which your students may be fluent."
                  name="secondaryLanguages"
                  disabled={isSubmitting}
                  inline={true}
                  inputWidth={1 / 2}
                  values={[
                    { label: 'French', value: 'French' },
                    { label: 'Spanish', value: 'Spanish' },
                    { label: 'Japanese', value: 'Japanese' },
                    { label: 'Portugese', value: 'Portugese' },
                    { label: 'Latin', value: 'Latin' },
                    { label: 'Chinese', value: 'Chinese' },
                    { label: 'Italian', value: 'Italian' },
                    { label: 'Arabic', value: 'Arabic' },
                    { label: 'Hindi', value: 'Hindi' },
                    { label: 'Russian', value: 'Russian' },
                    { label: 'Other', value: 'Other' },
                  ]}
                />
                {(values.secondaryLanguages as string[]).includes('Other') ? (
                  <Form.AssociatedField>
                    <FormField
                      name="otherSecondaryLanguages"
                      type="text"
                      placeholder="other languages, separated by commas"
                    />
                  </Form.AssociatedField>
                ) : null}
              </Form.Item>
            </Form.Row>
            <Form.ErrorComponent
              error={mutationError ? mutationError.message : null}
            />
            {submitCount > 0 ? <ErrorSummary errors={errors} /> : null}
            <Form.Row>
              <Form.Item width={1}>
                <Form.Button
                  type="submit"
                  disabled={isSubmitting}
                  onClick={submitForm}
                  align="right"
                >
                  {isSubmitting ? (
                    <Indicators.Spinner
                      size={'lg'}
                      className="mt-auto mr-2 mb-auto ml-auto"
                    />
                  ) : (
                    ''
                  )}
                  <span>Save and Submit</span>
                </Form.Button>
              </Form.Item>
            </Form.Row>
          </Form.Form>
        )
      }}
    </Formik>
  )
}

export const EducatorStepTwo = withRouter(FormView)
